/*eslint max-lines-per-function: ["error", 200]*/
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { ExamSearch } from '../Exam/ExamSearch';

import { AddExamToInvoiceFilters } from './AddExamToInvoiceFilters';

interface AddExamToInvoiceModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (examIds: string[]) => void;
}

export const AddExamToInvoiceModal: React.FC<AddExamToInvoiceModalProps> = ({
  show,
  onHide,
  onConfirm,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [companyId, setCompanyId] = useState<string | undefined>();
  const [dateFrom, setDateFrom] = useState<string | undefined>();
  const [dateTo, setDateTo] = useState<string | undefined>();
  const [withoutInvoiceAndReceipt, setWithoutInvoiceAndReceipt] =
    useState(false);
  const [completed, setCompleted] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const spd = useSpdCore();

  useEffect(() => {
    let mounted = true;
    spd.exams
      .getAllCount(
        undefined,
        undefined,
        undefined,
        undefined,
        true,
        companyId,
        dateFrom,
        dateTo,
        withoutInvoiceAndReceipt,
        completed,
      )
      .then(count => {
        if (mounted) {
          setTotalCount(count);
        }
      })
      .catch(console.error);
    return () => {
      mounted = false;
    };
  }, [
    spd.exams,
    companyId,
    dateFrom,
    dateTo,
    withoutInvoiceAndReceipt,
    completed,
  ]);

  const handleHide = () => {
    setSelectedIds([]);
    setCompanyId(undefined);
    setDateFrom(undefined);
    setDateTo(undefined);
    setWithoutInvoiceAndReceipt(false);
    setCompleted(false);
    onHide();
  };

  const handleClear = () => {
    setCompanyId(undefined);
    setDateFrom(undefined);
    setDateTo(undefined);
    setWithoutInvoiceAndReceipt(false);
    setCompleted(false);
  };

  const handleRowClick = (examId: string) => {
    setSelectedIds(prev =>
      prev.includes(examId)
        ? prev.filter(id => id !== examId)
        : [...prev, examId],
    );
  };

  const handleConfirm = () => {
    onConfirm(selectedIds);
    setSelectedIds([]);
  };

  const handleSelectAll = async () => {
    const ids = await spd.exams.getAllIds(
      undefined,
      undefined,
      true,
      companyId,
      dateFrom,
      dateTo,
      withoutInvoiceAndReceipt,
      completed,
    );
    setSelectedIds(ids);
  };

  const getData: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.exams.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        true,
        companyId,
        dateFrom,
        dateTo,
        withoutInvoiceAndReceipt,
        completed,
      ),

    [
      spd.exams,
      companyId,
      dateFrom,
      dateTo,
      withoutInvoiceAndReceipt,
      completed,
    ],
  );

  const getCount: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.exams.getAllCount(
        filterText,
        filterColumns,
        undefined,
        undefined,
        true,
        companyId,
        dateFrom,
        dateTo,
        withoutInvoiceAndReceipt,
        completed,
      ),

    [
      spd.exams,
      companyId,
      dateFrom,
      dateTo,
      withoutInvoiceAndReceipt,
      completed,
    ],
  );

  return (
    <Modal show={show} onHide={handleHide} dialogClassName="modal-90w">
      <Modal.Header closeButton>
        <Modal.Title>Wybierz badania do powiązania z fakturą</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <AddExamToInvoiceFilters
          companyId={companyId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          withoutInvoiceAndReceipt={withoutInvoiceAndReceipt}
          completed={completed}
          totalCount={totalCount}
          onCompanyChange={setCompanyId}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onWithoutInvoiceAndReceiptChange={setWithoutInvoiceAndReceipt}
          onCompletedChange={setCompleted}
          onSelectAll={handleSelectAll}
          onClear={handleClear}
        />
        <ExamSearch
          getData={getData}
          getCount={getCount}
          onRowClick={handleRowClick}
          withFilterInColumns={true}
          enableMultiSelect={true}
          selectedKeys={selectedIds}
          withoutButtons={true}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={selectedIds.length === 0}
        >
          Dodaj zaznaczone ({selectedIds.length})
        </Button>
        <Button variant="secondary" onClick={handleHide}>
          Anuluj
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
